import React, { useCallback } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';
import {
  BodyText,
  Button,
  formatDate,
  HighlightWords,
  theme,
} from '@sg/garnish';

import { useTelemetry, useTrackEffect } from '@order/Telemetry';
import { ignoreTimezone } from '@order/utils';

import { type WantedTime } from '../../../machines/ordering-machine.types';
import { adjustWantedTime } from '../../../utils';

/**
 * Warning message for the bag.
 */
export const BagTimeslotUnavailableWarning = (
  props: BagTimeslotUnavailableWarningProps,
) => {
  const {
    message: rawMessage,
    wantedTime,
    previousWantedTime,
    continueCta,
    backCta,
    onContinue,
    onBack,
  } = props;

  const { track } = useTelemetry();

  const { message, highlights } = prepareMessage(
    rawMessage,
    wantedTime,
    previousWantedTime,
  );

  useTrackEffect('bag.timeslot-unavailable.view');

  const handleOnContinue = useCallback(() => {
    track('bag.timeslot-unavailable.continue.pressed');
    onContinue();
  }, [onContinue, track]);

  const handleOnBack = useCallback(() => {
    track('bag.timeslot-unavailable.back.pressed');
    onBack();
  }, [onBack, track]);

  return (
    <SafeAreaView style={styles.container} edges={SAFE_AREA_EDGES}>
      <HighlightWords
        sizeMatch={['24']}
        text={message}
        highlights={highlights}
      />

      <View style={styles.buttonsContainer}>
        <Button
          size="large-wide"
          palette="primary"
          accessibilityLabel={continueCta}
          onPress={handleOnContinue}
        >
          <BodyText style={styles.continueCtaColor} sizeMatch={['18']}>
            {continueCta}
          </BodyText>
        </Button>

        <Button
          size="large-wide"
          palette="primaryOutline"
          accessibilityLabel={backCta}
          onPress={handleOnBack}
        >
          <BodyText style={styles.backCtaColor} sizeMatch={['18']}>
            {backCta}
          </BodyText>
        </Button>
      </View>
    </SafeAreaView>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const prepareMessage = (
  message: string,
  wantedTime?: WantedTime,
  previousWantedTime?: WantedTime,
) => {
  const previous = getWantedTimeString(previousWantedTime);
  const current = getWantedTimeString(wantedTime);

  return {
    message: message
      .replace('{previous}', previous)
      .replace('{current}', current),
    highlights: [previous, current],
  };
};

const getWantedTimeString = (wantedTime?: WantedTime) => {
  const adjustedTime = adjustWantedTime(wantedTime);
  const validTime = adjustedTime ? ignoreTimezone(adjustedTime) : null;

  return validTime ? formatDate(validTime, 'h:mma') : '';
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edges = Platform.select({
  android: ['bottom'],
  default: [],
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: theme.spacing['6'],
    paddingVertical: theme.spacing['10'],
    gap: theme.spacing['6'],
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonsContainer: {
    gap: theme.spacing['4'],
  },
  continueCtaColor: {
    color: theme.colors.WHITE,
  },
  backCtaColor: {
    color: theme.colors.KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type BagTimeslotUnavailableWarningProps = {
  wantedTime?: WantedTime;
  previousWantedTime?: WantedTime;
  message: string;
  continueCta: string;
  backCta: string;
  onContinue: () => void;
  onBack: () => void;
};
